// src/pages/TimeReportPopup.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useAuth } from '../navigation/AuthProvider';
import { FaEdit } from 'react-icons/fa';
import axios from 'axios';
import { BACKEND_URL } from '../constants';
import * as XLSX from 'xlsx';
import EditFromTimeReport from './EditFromTimeReport';

// ✅ CENTRAL: all karens logic must live here (module returns perDate + perShift + month max)
// import calculateKarensForEmployee from "../utils/absence/karensCalculate";

export const TimeReportPopup = ({ employee, selectedMonth, selectedYear, selectedTimeSchema, shifts, onClose, readOnly }) => {
  const {
    draftSchedule,
    obs,
    projects,
    pays,
    vacationPay,
    overtimeRules,
    timeSchemas,
    absenceRules,
    user,
    company,
    setDraftSchedule
  } = useAuth();

  // ✅ strict: only explicit true means enabled
  const karensEnabled = company?.payrollSettings?.karensEnabled === true;

  const [rows, setRows] = useState([]);
  const [editedRows, setEditedRows] = useState([]);
  const [obTotals, setObTotals] = useState({});
  const [obPayTotals, setObPayTotals] = useState({});
  const [currentMonth, setCurrentMonth] = useState('');
  const [allPays, setAllPays] = useState(pays || []);
  const [allTimeSchemas, setAllTimeSchemas] = useState(timeSchemas || []);
  const [allOvertimeRules, setAllOvertimeRules] = useState(overtimeRules || []);
  const [showPayData, setShowPayData] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [selectedShiftData, setSelectedShiftData] = useState(null);
  const [editType, setEditType] = useState('');
  const [showEditShiftPopup, setShowEditShiftPopup] = useState(false);

  // ✅ karensram (max) for the month/period (computed inside karensCalculate)
  const [karensMaxHours, setKarensMaxHours] = useState(0);

  const [localSelectedMonth, setLocalSelectedMonth] = useState(selectedMonth);
  const [localSelectedYear, setLocalSelectedYear] = useState(selectedYear);

  // ✅ Print option - include/exclude totals panel (bottom rows: Sjukfrånvaro/Frånvaro/Övertid incl. Karens)
  const [includeTotalsInPrint, setIncludeTotalsInPrint] = useState(true);

  // ✅ NEW: Print confirm popup (before printing)
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [pendingIncludeTotalsInPrint, setPendingIncludeTotalsInPrint] = useState(true);

  const openPrintConfirm = () => {
    setPendingIncludeTotalsInPrint(Boolean(includeTotalsInPrint));
    setShowPrintConfirm(true);
  };

  const cancelPrintConfirm = () => {
    setShowPrintConfirm(false);
  };

  const printRef = useRef(null);

  // ✅ snapshot of rows when entering edit mode (for "Avbryt" restore)
  const originalRowsRef = useRef(null);

  // --- Safe number formatting (prevents ALL toFixed crashes) ---
  const toNum = (v, fb = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fb;
  };
  const h2 = (v) => toNum(v, 0).toFixed(2);
  const h1 = (v) => toNum(v, 0).toFixed(1);
  const kr2 = (v) => `${h2(v)}kr`;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Tidsrapport_${employee?.name || ''}_${localSelectedMonth}_${localSelectedYear}`,
    pageStyle: `
    @page {
      size: A4 portrait;
      margin: 12mm 10mm 12mm 10mm;

      @top-right {
        content: "Sida " counter(page) " / " counter(pages);
        font-size: 9px;
      }
    }

    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        background-color: #fff !important;
      }

      .time-report-print {
        width: 190mm !important;
        max-width: 190mm !important;
        margin: 0 auto !important;
        padding: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }

      .time-report-print .time-report-table-wrapper {
        max-height: none !important;
        overflow: visible !important;
      }

      .time-report-print table {
        width: 100% !important;
        border-collapse: collapse !important;
        font-size: 8px !important;
        page-break-inside: auto !important;
        table-layout: fixed !important;
        background-color: #fff !important;
      }

      .time-report-print thead {
        display: table-header-group !important;
      }

      .time-report-print tfoot {
        display: table-row-group !important;
      }

      .time-report-print tr {
        page-break-inside: avoid !important;
        page-break-after: auto !important;
      }

      .time-report-print th,
      .time-report-print td {
        border: 1px solid #ccc !important;
        padding: 6px 3px !important;
        text-align: center !important;
        vertical-align: middle !important;
        white-space: nowrap !important;
      }

      /* ✅ Print: allow longer readable labels before ellipsis inside cells */
      .absence-label,
      .overtime-label {
        max-width: none !important;
      }

      /* Dölj OB-dagar och tider helt */
      .ob-days,
      .ob-time {
        display: none !important;
      }

      /* ===== CLEAN PRINT STACK FOR ABSENCE + OVERTIME ===== */
      .absence-cell div,
      .overtime-cell div {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1 !important;
        gap: 0 !important;
      }

      .absence-label,
      .overtime-label {
        display: block !important;
        font-size: 6px !important;
        font-weight: 600 !important;
        text-align: center !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .absence-time,
      .overtime-time {
        display: block !important;
        font-size: 7px !important;
        text-align: center !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .absence-cell div + div,
      .overtime-cell div + div {
        margin-top: 4px !important;
      }

      .absence-cell span,
      .overtime-cell span {
        background: none !important;
        background-color: transparent !important;
        box-shadow: none !important;
        border: none !important;
      }

      .absence-cell span:last-child,
      .overtime-cell span:last-child {
        font-size: 7px !important;
        opacity: 0.85 !important;
        display: block !important;
        color: #000 !important;
      }

      .time-report-print .absence-label,
      .time-report-print .overtime-label,
      .time-report-print .absence-cell span,
      .time-report-print .overtime-cell span {
        background: none !important;
        background-color: transparent !important;
        color: #000 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        padding: 0 !important;
      }

      /* Dölj edit-ikoner och länkar i cellerna */
      .time-report-print svg {
        display: none !important;
      }

      .time-report-print td p {
        display: none !important;
      }

      /* Dölj knappar & UI-element */
      .time-report-print button {
        display: none !important;
      }

      /* ✅ Show totals panel in print, and format it compactly */
      .time-report-footer {
        display: block !important;
        margin-top: 10px !important;
        border-top: 1px solid #ddd !important;
        padding-top: 8px !important;

        /* ✅ keep together */
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }



      /* ✅ Keep the entire totals panel together on one page */
      .time-report-footer {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }

      /* ✅ Extra safety: prevent splitting within the grid/cards too */
      .totals-grid,
      .totals-card {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }

      /* ✅ If a browser tries to be clever with grid pagination, this helps */
      .totals-grid * {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }


      /* ✅ If user opts OUT, hide totals panel entirely in print */
      .print-hide-totals .time-report-footer {
        display: none !important;
      }

      /* ✅ print: enforce exact 3 equal columns (33% each) */
      .totals-grid {
        display: grid !important;
        grid-template-columns: 1fr 1fr 1fr !important;
        gap: 0 !important; /* ✅ no gap between cards */
      }

      .totals-card {
        border: 1px solid #e5e7eb !important;
        border-radius: 0 !important; /* ✅ squared border */
        padding: 8px !important;
        box-shadow: none !important;
      }

      /* ✅ print: bottom section headers smaller */
      .totals-card-title {
        font-size: 9px !important;
        font-weight: 900 !important;
        line-height: 1 !important;
        margin: 0 !important;
      }

      .totals-card--sick .totals-list-grid {
        grid-template-columns: 1fr 1fr !important;
      }

      .totals-card-total {
        font-size: 9px !important;
        line-height: 1 !important;
        margin: 0 !important;
      }

      .totals-row {
        font-size: 8px !important;
        padding: 2px 0 !important;
      }

      .totals-chip-label,
      .totals-chip-value,
      .totals-grid-item-label,
      .totals-grid-item-value {
        font-size: 8px !important;
      }

      .hide-in-print {
        display: none !important;
      }
    }

    /* Hide new header on screen */
    .print-only-header {
      display: none;
    }

    @media print {
      .print-only-header {
        display: block !important;
        background-color: #fff !important;
      }

      .print-header-wrapper {
        text-align: left;
        padding: 8px 0;
      }

      .print-header-title {
        font-size: 24px;
        margin: 0;
        font-weight: 700;
      }

      .print-header-subtitle {
        font-size: 18px;
        margin: 4px 0 0 0;
        font-weight: 700;
      }

      .time-report-table-wrapper table {
        margin-top: 10px;
      }

      .hide-original-header-print {
        display: none !important;
      }

      .print-only-header th {
        border: none !important;
        background: none !important;
        box-shadow: none !important;
      }
    }
    `,
  });

  // ✅ Confirm and print (after user chooses bottom rows include/exclude)
  const confirmAndPrint = () => {
    setIncludeTotalsInPrint(Boolean(pendingIncludeTotalsInPrint));
    setShowPrintConfirm(false);

    // Ensure DOM/class updates before print
    requestAnimationFrame(() => {
      handlePrint();
    });
  };

  // ✅ resolve activation flags for THIS employee (default true)
  const resolvedObActivated = useMemo(() => {
    return typeof employee?.payrollSettings?.includeOB === "boolean"
      ? employee.payrollSettings.includeOB
      : true;
  }, [employee]);

  const resolvedVacationActivated = useMemo(() => {
    return typeof employee?.payrollSettings?.includeVacation === "boolean"
      ? employee.payrollSettings.includeVacation
      : true;
  }, [employee]);

  // ✅ Total OB hours (sum of all OB categories)
  const totalObHours = useMemo(() => {
    if (!resolvedObActivated) return 0;
    return Object.values(obTotals || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
  }, [obTotals, resolvedObActivated]);

  // ✅ resolve pay type + base amounts (hourly vs monthly)
  const selectedPayObj = useMemo(() => {
    return allPays.find(p => p._id === employee?.pay) || null;
  }, [allPays, employee]);

  const payType = selectedPayObj?.payType || "hourly"; // "hourly" | "monthly"
  const monthlyAmount = Number(selectedPayObj?.amount || 0);

  // Mappar absenceRule._id -> name för snabb rendering
  const absenceNameById = useMemo(() => {
    const m = {};
    (absenceRules || []).forEach(r => { m[String(r._id)] = r.name; });
    return m;
  }, [absenceRules]);

  // ✅ map absenceRuleId -> systemKey (used for short labels + karens)
  const absenceSystemKeyById = useMemo(() => {
    const m = {};
    (absenceRules || []).forEach(r => { m[String(r._id)] = r.systemKey || null; });
    return m;
  }, [absenceRules]);

  // ✅ short label (prevents “Sjukfrånvaro” from being cut off)
  const shortAbsenceLabel = (ruleName, systemKey) => {
    if (systemKey === "sick") return "Sjuk";
    const n = (ruleName || "").toLowerCase();
    if (n.includes("sjuk")) return "Sjuk";
    if (n.includes("vab")) return "VAB";
    if (n.includes("semester")) return "Sem";
    if (n.includes("föräld")) return "FP";
    if (n.includes("tjänstled")) return "TL";
    const s = (ruleName || "Frånvaro").trim();
    return s.length > 10 ? s.slice(0, 10) + "…" : s;
  };

  const dayAbbrevMap = {
    måndag: 'Mån',
    tisdag: 'Tis',
    onsdag: 'Ons',
    torsdag: 'Tors',
    fredag: 'Fre',
    lördag: 'Lör',
    söndag: 'Sön'
  };

  // --- Helpers: validering & tid ---
  const isValidTime = (t) => typeof t === 'string' && /^\d{2}:\d{2}$/.test(t);

  const parseTimeToMinutes = (t) => {
    if (!isValidTime(t)) return null;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // Normalisera [from,to] till samma tidslinje som skiftet [sMin,eMin]
  const normalizeForShift = (fromMin, toMin, sMin, eMin) => {
    let f = fromMin;
    let t = toMin;

    if (t <= f) t += 1440;

    if (eMin > 1440 && t <= sMin) {
      f += 1440;
      t += 1440;
    }
    return [f, t];
  };

  const isShiftCompleteForReports = (s, employeeId) => Boolean(
    s &&
    s.status === "complete" &&
    s._id &&
    s.project &&
    s.date &&
    isValidTime(s.startTime) &&
    isValidTime(s.endTime) &&
    Array.isArray(s.employees) &&
    s.employees.some(e => e?.employeeId === employeeId)
  );

  const getBreakMinutesFromArray = (breaks) => {
    if (!Array.isArray(breaks) || breaks.length === 0) return 0;
    return breaks.reduce((total, b) => {
      if (!isValidTime(b?.from) || !isValidTime(b?.to)) return total;
      const from = parseTimeToMinutes(b.from);
      let to = parseTimeToMinutes(b.to);
      if (to <= from) to += 1440;
      return total + (to - from);
    }, 0);
  };

  // ✅ monthly hourly equivalent based on planned hours in this month
  const monthlyHourlyEq = useMemo(() => {
    if (payType !== "monthly") return 0;

    const filtered = (shifts || [])
      .filter((sh) => {
        const [y, m] = String(sh.date).split('-').map(Number);
        return (
          isShiftCompleteForReports(sh, employee?._id) &&
          y === localSelectedYear &&
          m === localSelectedMonth &&
          (!selectedTimeSchema || sh.timeSchema === selectedTimeSchema)
        );
      });

    let plannedMinutesMonth = 0;

    filtered.forEach((sh) => {
      const emp = sh.employees.find((e) => e.employeeId === employee._id);
      if (!emp) return;

      const sMin = parseTimeToMinutes(sh.startTime);
      let eMin = parseTimeToMinutes(sh.endTime);
      if (eMin <= sMin) eMin += 1440;

      const grossMinutes = eMin - sMin;

      const plannedBreakMinutes = getBreakMinutesFromArray(emp?.breaks);
      const plannedWorkMinutes = Math.max(grossMinutes - plannedBreakMinutes, 0);

      plannedMinutesMonth += plannedWorkMinutes;
    });

    const plannedHoursMonth = plannedMinutesMonth / 60;
    const DEFAULT_FALLBACK_HOURS = 160;

    if (plannedHoursMonth > 0) return monthlyAmount / plannedHoursMonth;
    return monthlyAmount / DEFAULT_FALLBACK_HOURS;
  }, [
    payType,
    monthlyAmount,
    shifts,
    employee,
    localSelectedMonth,
    localSelectedYear,
    selectedTimeSchema
  ]);

  useEffect(() => {
    if (employee && localSelectedMonth && localSelectedYear) {
      getData();
    }
    return () => {
      setRows([]);
      setObTotals({});
      setCurrentMonth('');
      setKarensMaxHours(0); // ✅ reset for next open
      setShowPrintConfirm(false); // ✅ reset
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, localSelectedMonth, localSelectedYear, selectedTimeSchema, payType, monthlyAmount, monthlyHourlyEq]);











 // --- EXPORT TO EXCEL ---
  const exportToExcel = (type) => {
    const obsForExcel = (obs || [])
      .slice()
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));

    const overtimeNameById = {};
    (allOvertimeRules || overtimeRules || []).forEach((r) => {
      overtimeNameById[String(r._id)] = r.name;
    });

    const period = `${currentMonth} ${localSelectedYear}`;
    const personRow = ["Person:", employee.name];
    const periodRow = ["Period:", period];
    const blankRow = [];

    let headers, dataRows, sheetName, fileName;

    const filteredRows = rows.filter((r) => r.day);

    if (type === "time") {
      headers = [
        "Datum",
        "Arbetsplats",
        "Arbetstid",
        "Bruttotid",
        "Rast",
        "Övertid (tider)",
        "Övertid (h)",
        "Frånvaro (tider)",
        "Frånvaro (h)",
        "Karens (h)",
        "Total tid",
        ...obsForExcel.map((o) => o.name),
      ];

      dataRows = filteredRows.map((r) => {
        const overtimeText = (r.overtime || [])
          .map((ot) => {
            const name = overtimeNameById[String(ot.type)] || "Övertid";
            const from = ot?.from || "";
            const to = ot?.to || "";
            return `${name}: ${from}-${to}`;
          })
          .join(", ");

        const absenceText = (r.absences || [])
          .map((abs) => {
            const name = absenceNameById[String(abs.absenceType)] || "Frånvaro";
            const time = abs?.appliesWholeShift
              ? `${r.startTime}-${r.endTime}`
              : `${abs?.from || ""}-${abs?.to || ""}`;
            return `${name}: ${time}`;
          })
          .join(", ");

        return [
          r.day,
          r.workplace,
          r.startTime && r.endTime ? `${r.startTime}–${r.endTime}` : "",
          toNum(r.grossTime || 0),
          (r.breakRange || []).join(", "),
          overtimeText,
          Number((toNum(r.overtimeHours ?? 0)).toFixed(2)),
          absenceText,
          Number((toNum(r.absenceHours ?? 0)).toFixed(2)),
          Number((toNum(r.karensHours ?? 0)).toFixed(2)),
          toNum(r.totalWorked || 0),
          ...obsForExcel.map((o) => toNum(r.obDetails?.[o._id.toString()] || 0)),
        ];
      });

      sheetName = "Tidsrapport";
      fileName = `Tidsrapport_${employee.name}_${localSelectedMonth}_${localSelectedYear}.xlsx`;
    } else {
      headers = [
        "Datum",
        "Arbetsplats",
        "Arbetstid",
        "Bruttolön",
        "Rastavdrag",
        "Övertidstillägg",
        "Semesterersättning",
        ...obsForExcel.map((o) => o.name),
        "Frånvaro",
        "Total ersättning",
      ];

      dataRows = filteredRows.map((r) => [
        r.day,
        r.workplace,
        r.startTime && r.endTime ? `${r.startTime}–${r.endTime}` : "",
        Number(toNum(r.baseSalary + r.breakDeduction).toFixed(2)),
        Number(toNum(r.breakDeduction === 0 ? r.breakDeduction : -r.breakDeduction).toFixed(2)),
        Number(toNum(r.overtimePay).toFixed(2)),
        Number(toNum(r.vacationPay).toFixed(2)),
        ...obsForExcel.map((o) => Number(toNum(r.obDetailsPay?.[o._id.toString()] || 0).toFixed(2))),
        Number(toNum(-r.absenceLoss).toFixed(2)),
        Number(toNum(r.totalPay).toFixed(2)),
      ]);

      sheetName = "Lönrapport";
      fileName = `Lönrapport_${employee.name}_${localSelectedMonth}_${localSelectedYear}.xlsx`;
    }

    const aoa = [personRow, periodRow, blankRow, headers, ...dataRows, blankRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    const headerRowExcel = 4;
    const dataStartRowExcel = headerRowExcel + 1;
    const dataEndRowExcel = dataStartRowExcel + filteredRows.length - 1;
    const sumRowExcel = dataEndRowExcel + 2;

    XLSX.utils.sheet_add_aoa(ws, [["SUMMA:"]], { origin: `A${sumRowExcel}` });

    const setSumFormula = (colIdx, numberFmt = "0.00") => {
      if (filteredRows.length <= 0) return;
      const colLetter = XLSX.utils.encode_col(colIdx);
      const from = `${colLetter}${dataStartRowExcel}`;
      const to = `${colLetter}${dataEndRowExcel}`;
      const addr = `${colLetter}${sumRowExcel}`;
      ws[addr] = ws[addr] || {};
      ws[addr].t = "n";
      ws[addr].f = `SUM(${from}:${to})`;
      ws[addr].z = numberFmt;
    };

    if (type === "time") {
      setSumFormula(3);
      setSumFormula(6);
      setSumFormula(8);
      setSumFormula(9);
      setSumFormula(10);

      const obStartCol = 11;
      for (let i = 0; i < obsForExcel.length; i++) setSumFormula(obStartCol + i);
    } else {
      setSumFormula(3);
      setSumFormula(4);
      setSumFormula(5);
      setSumFormula(6);

      const obStartCol = 7;
      for (let i = 0; i < obsForExcel.length; i++) setSumFormula(obStartCol + i);

      const absenceCol = obStartCol + obsForExcel.length;
      const totalPayCol = absenceCol + 1;
      setSumFormula(absenceCol);
      setSumFormula(totalPayCol);
    }

    Object.keys(ws).forEach((cell) => {
      if (!cell.startsWith("!")) {
        ws[cell].s = {
          alignment: { wrapText: true, vertical: "top" },
        };
      }
    });

    const baseCols =
      type === "time"
        ? [
          { wch: 10 },
          { wch: 22 },
          { wch: 14 },
          { wch: 10 },
          { wch: 10 },
          { wch: 34 },
          { wch: 10 },
          { wch: 34 },
          { wch: 10 },
          { wch: 10 },
          { wch: 10 },
        ]
        : [
          { wch: 10 },
          { wch: 22 },
          { wch: 14 },
          { wch: 12 },
          { wch: 12 },
          { wch: 14 },
          { wch: 16 },
        ];

    const obCols = obsForExcel.map(() => ({ wch: 12 }));
    const tailCols = type === "time" ? [] : [{ wch: 12 }, { wch: 14 }];
    ws["!cols"] = [...baseCols, ...obCols, ...tailCols];

    ws["!freeze"] = { xSplit: 0, ySplit: 4 };
    ws["!pageSetup"] = { orientation: "landscape", fitToWidth: 1, fitToHeight: 0 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };
  // --- END EXPORT ---











  const handleClickedEditShift = (e, type, shiftId, empId, index = null) => {
    setEditType(type);
    const shift = (draftSchedule || []).find((s) => s._id === shiftId);
    setSelectedShiftData({ shift, empId, index });
    setPopupPosition({ x: e.clientX, y: e.clientY });
    setShowEditShiftPopup(true);
  };

  const handleEditPopupClose = (updatedData) => {
    setShowEditShiftPopup(false);
    if (!updatedData) return;

    const { shiftId, empId, index } = updatedData;

    const currentRow = rows.find((r) => r.shiftId === shiftId && r.empId === empId) || {};
    const updatedFields = getUpdatedFieldsFromPopup(editType, updatedData, index, currentRow);

    setRows((prev) =>
      prev.map((row) => {
        if (row.shiftId === shiftId && row.empId === empId) {
          return { ...row, ...updatedFields };
        }
        return row;
      })
    );

    setEditedRows((prev) => {
      const key = `${shiftId}_${empId}`;
      return {
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          ...updatedFields,
        },
      };
    });
  };

  const getUpdatedFieldsFromPopup = (editType, data, index = null, currentRow = {}) => {
    const preservedFields = {
      breaks: currentRow.breaks || [],
      breakRange: currentRow.breakRange || [],
      breakTime: currentRow.breakTime || 0,
      overtime: currentRow.overtime || [],
      overtimeRanges: currentRow.overtimeRanges || [],
      absences: currentRow.absences || [],
      absenceRanges: currentRow.absenceRanges || [],
      absenceNames: currentRow.absenceNames || [],
      startTime: currentRow.startTime,
      endTime: currentRow.endTime,
    };

    let updated = {};

    switch (editType) {
      case 'break': {
        const newBreak = { from: data.breakStartTime, to: data.breakEndTime };
        const updatedBreaks = [...(currentRow.breaks || [])];
        if (index !== null) {
          updatedBreaks[index] = newBreak;
        } else {
          updatedBreaks.push(newBreak);
        }

        updated = {
          breaks: updatedBreaks,
          breakRange: updatedBreaks.map(b => `${b.from} - ${b.to}`),
          breakTime: updatedBreaks.reduce((sum, b) => sum + calculateBreakHours(b.from, b.to), 0),
        };
        break;
      }

      case 'overtime': {
        const newOvertime = {
          from: data.overtimeFrom,
          to: data.overtimeTo,
          type: data.overtimeType,
          source: 'manual',
        };
        const updatedOvertime = [...(currentRow.overtime || [])];
        if (index !== null) {
          updatedOvertime[index] = newOvertime;
        } else {
          updatedOvertime.push(newOvertime);
        }
        updated = {
          overtime: updatedOvertime,
          overtimeRanges: updatedOvertime.map(o => `${o.from} - ${o.to}`),
        };
        break;
      }

      case 'absence': {
        const newAbsence = {
          from: data.absenceFrom,
          to: data.absenceTo,
          appliesWholeShift: data.appliesWholeShift,
          absenceType: data.absenceType,
        };
        const updatedAbsences = [...(currentRow.absences || [])];
        if (index !== null) {
          updatedAbsences[index] = newAbsence;
        } else {
          updatedAbsences.push(newAbsence);
        }

        updated = {
          absences: updatedAbsences,
          absenceRanges: updatedAbsences.map((a) =>
            a.appliesWholeShift ? `${currentRow.startTime} - ${currentRow.endTime}` : `${a.from} - ${a.to}`
          ),
          absenceNames: updatedAbsences.map((a) => {
            const rule = (absenceRules || []).find((r) => String(r._id) === String(a.absenceType));
            return rule?.name || 'Frånvaro';
          }),
        };
        break;
      }

      case 'shift':
        updated = {
          startTime: data.startTime,
          endTime: data.endTime,
        };
        break;

      default:
        break;
    }

    return {
      ...preservedFields,
      ...updated,
    };
  };

  const calculateBreakHours = (from, to) => {
    if (!isValidTime(from) || !isValidTime(to)) return 0;
    const [fh, fm] = from.split(':').map(Number);
    const [th, tm] = to.split(':').map(Number);
    let start = fh * 60 + fm;
    let end = th * 60 + tm;
    if (end <= start) end += 1440;
    return (end - start) / 60;
  };

  const handleSubmitEdits = async () => {
    const editedShiftUpdates = Object.entries(editedRows)
      .map(([key, changes]) => {
        const [shiftId, empId] = key.split('_');
        const row = rows.find((r) => r.shiftId === shiftId && r.empId === empId);
        if (!row) return null;

        return {
          shiftId,
          employeeId: empId,
          date: row.date,
          project: row.projectId,
          startTime: changes.startTime || row.startTime,
          endTime: changes.endTime || row.endTime,
          breaks: changes.breaks || row.breaks || [],
          overtime: changes.overtime || row.overtime || [],
          absences: changes.absences || row.absences || [],
        };
      })
      .filter(Boolean);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/shifts/bulk-update`, {
        updates: editedShiftUpdates,
        userId: user._id,
        companyId: company._id,
      });

      if (data.status === "success") {
        localStorage.setItem("reopenTimeReportPopup", JSON.stringify({
          employeeId: employee._id,
          selectedMonth: localSelectedMonth,
          selectedYear: localSelectedYear
        }));

        setDraftSchedule((prev) => {
          const updatedMap = new Map((data.updatedShifts || []).map((s) => [s._id, s]));
          return (prev || []).map((s) => (updatedMap.has(s._id) ? updatedMap.get(s._id) : s));
        });

        return window.location.reload();
      } else {
        return alert("Kunde inte spara ändringar: " + (data.errorText || ""));
      }
    } catch (err) {
      console.error('❌ Failed to update shifts', err);
    }
  };

  const handlePrevMonth = () => {
    let m = localSelectedMonth - 1;
    let y = localSelectedYear;

    if (m === 0) {
      m = 12;
      y = y - 1;
    }

    setLocalSelectedMonth(m);
    setLocalSelectedYear(y);
  };

  const handleNextMonth = () => {
    let m = localSelectedMonth + 1;
    let y = localSelectedYear;

    if (m === 13) {
      m = 1;
      y = y + 1;
    }

    setLocalSelectedMonth(m);
    setLocalSelectedYear(y);
  };

  const calculateTotalHours = (start, end) => {
    if (!isValidTime(start) || !isValidTime(end)) return 0;
    const [h1_, m1] = start.split(':').map(Number);
    const [h2_, m2_] = end.split(':').map(Number);
    let s = h1_ * 60 + m1;
    let e = h2_ * 60 + m2_;
    if (e <= s) e += 1440;
    return (e - s) / 60;
  };

  const calculateOvertimeHoursDetailed = (overtimeArr, shiftStartMin, shiftEndMin) => {
    if (!Array.isArray(overtimeArr)) return { insideShiftMinutes: 0, outsideShiftMinutes: 0 };

    let normShiftStart = shiftStartMin;
    let normShiftEnd = shiftEndMin;
    if (normShiftEnd <= normShiftStart) normShiftEnd += 1440;

    let insideShiftMinutes = 0;
    let outsideShiftMinutes = 0;

    overtimeArr.forEach(ot => {
      if (!ot.from || !ot.to) return;

      const [fH, fM] = ot.from.split(':').map(Number);
      const [tH, tM] = ot.to.split(':').map(Number);
      let fromMin = fH * 60 + fM;
      let toMin = tH * 60 + tM;
      if (toMin <= fromMin) toMin += 1440;

      if (normShiftEnd > 1440 && fromMin < normShiftStart) {
        fromMin += 1440;
        toMin += 1440;
      }

      for (let m = fromMin; m < toMin; m++) {
        if (m >= normShiftStart && m < normShiftEnd) {
          insideShiftMinutes += 1;
        } else {
          outsideShiftMinutes += 1;
        }
      }
    });

    return { insideShiftMinutes, outsideShiftMinutes };
  };

  const calcOvertimeArrayHours = (arr) => {
    if (!Array.isArray(arr)) return 0;
    return arr.reduce((sum, ot) => {
      const from = String(ot?.from || "");
      const to = String(ot?.to || "");
      if (!isValidTime(from) || !isValidTime(to)) return sum;

      const [fh, fm] = from.split(":").map(Number);
      const [th, tm] = to.split(":").map(Number);
      let f = fh * 60 + fm;
      let t = th * 60 + tm;
      if (t <= f) t += 1440;

      return sum + (t - f) / 60;
    }, 0);
  };

  const calculateObTotals = (data) => {
    const totals = {};
    data.forEach((r) => {
      Object.entries(r.obDetails || {}).forEach(([k, v]) => {
        totals[k] = (totals[k] || 0) + (Number(v) || 0);
      });
    });
    setObTotals(totals);
  };

  const calculateObPayTotals = (data) => {
    const totals = {};
    data.forEach((r) => {
      Object.entries(r.obDetailsPay || {}).forEach(([k, v]) => {
        totals[k] = (totals[k] || 0) + (Number(v) || 0);
      });
    });
    setObPayTotals(totals);
  };

  // ✅ centralized "start edit" (take snapshot)
  const startEditing = () => {
    try {
      originalRowsRef.current = JSON.parse(JSON.stringify(rows));
    } catch (e) {
      originalRowsRef.current = rows;
    }
    setEditedRows({});
    setIsEditing(true);
  };

  // ✅ "Avbryt" should restore snapshot + clear edits + close edit popup
  const cancelEditing = () => {
    const snap = originalRowsRef.current;

    if (Array.isArray(snap)) {
      setRows(snap);
      calculateObTotals(snap);
      calculateObPayTotals(snap);
    }

    setEditedRows({});
    setShowEditShiftPopup(false);
    setSelectedShiftData(null);
    setEditType('');
    setIsEditing(false);
  };

  // ✅ month bounds for karens calc
  const getMonthBounds = (year, month) => {
    const y = Number(year);
    const m = Number(month);
    const from = new Date(Date.UTC(y, m - 1, 1));
    const to = new Date(Date.UTC(y, m, 0));
    const pad2 = (n) => String(n).padStart(2, "0");
    const fromStr = `${from.getUTCFullYear()}-${pad2(from.getUTCMonth() + 1)}-${pad2(from.getUTCDate())}`;
    const toStr = `${to.getUTCFullYear()}-${pad2(to.getUTCMonth() + 1)}-${pad2(to.getUTCDate())}`;
    return { fromStr, toStr };
  };

  // REPLACE the whole getData function
  const getData = () => {
    const filtered = (shifts || [])
      .filter((sh) => {
        const [y, m] = String(sh.date).split('-').map(Number);
        return (
          isShiftCompleteForReports(sh, employee._id) &&
          y === localSelectedYear &&
          m === localSelectedMonth &&
          (!selectedTimeSchema || sh.timeSchema === selectedTimeSchema)
        );
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const monthName = new Date(localSelectedYear, localSelectedMonth - 1).toLocaleString('sv-SE', { month: 'long' });
    setCurrentMonth(monthName.charAt(0).toUpperCase() + monthName.slice(1));

    const payRate = payType === "monthly" ? (monthlyHourlyEq || 0) : (monthlyAmount || 0);

    const vacRate =
      vacationPay?.vacationPayActive && resolvedVacationActivated
        ? vacationPay.vacationPayPercentage / 100
        : 0;

    const monthBounds = getMonthBounds(localSelectedYear, localSelectedMonth);

    // ✅ Only compute karens when enabled; otherwise keep empty maps + 0 max
    let karensResult = { perDate: {}, perShift: {}, summary: { monthKarensMaxHours: 0 } };

    // if (karensEnabled) {
    //   karensResult = calculateKarensForEmployee({
    //     company,
    //     employeeId: employee._id,
    //     shifts: filtered,
    //     absenceRules: absenceRules || [],
    //     monthBounds,
    //     overrides: null,
    //     debug: false,
    //   });
    // }

    const karensPerDate = karensResult?.perDate || {};
    const karensPerShift = karensResult?.perShift || {};

    // ✅ store karensram (max) for bottom summary + print (computed in module)
    setKarensMaxHours(karensEnabled ? toNum(karensResult?.summary?.monthKarensMaxHours, 0) : 0);

    const out = filtered.map((sh) => {
      const emp = sh.employees.find((e) => e.employeeId === employee._id);

      const sMinRaw = parseTimeToMinutes(sh.startTime);
      const eMinRaw = parseTimeToMinutes(sh.endTime);
      let sMin = sMinRaw, eMin = eMinRaw;
      if (eMin <= sMin) eMin += 1440;

      const calculateAbsenceHoursLocal = (absence) => {
        if (!absence || !absence.absenceType) return 0;

        let shiftStart = sMin;
        let shiftEnd = eMin;

        if (absence.appliesWholeShift) return (shiftEnd - shiftStart) / 60;
        if (!absence.from || !absence.to) return 0;

        const toMinLocal = (t) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };

        let aS = toMinLocal(absence.from);
        let aE = toMinLocal(absence.to);

        const [nFrom, nTo] = normalizeForShift(aS, aE, shiftStart, shiftEnd);

        const overlapStart = Math.max(shiftStart, nFrom);
        const overlapEnd = Math.min(shiftEnd, nTo);
        return Math.max(0, overlapEnd - overlapStart) / 60;
      };

      const absencesWithHours = (emp?.absences || []).map((abs) => ({
        ...abs,
        hours: calculateAbsenceHoursLocal(abs),
      }));

      const absenceHours = absencesWithHours.reduce((sum, abs) => sum + (Number(abs.hours) || 0), 0);

      const grossH = calculateTotalHours(sh.startTime, sh.endTime);

      const breakMinutes = getBreakMinutesFromArray(emp?.breaks);
      const breakH = breakMinutes / 60;
      const breakR = (emp?.breaks || [])
        .filter(b => isValidTime(b?.from) && isValidTime(b?.to))
        .map(b => `${b.from} - ${b.to}`);

      const { insideShiftMinutes, outsideShiftMinutes } =
        calculateOvertimeHoursDetailed(emp?.overtime, sMin, eMin);
      const otH = (insideShiftMinutes + outsideShiftMinutes) / 60;

      const overlapAdjustedGrossH = Math.max(grossH - (insideShiftMinutes / 60), 0);

      const otRanges = (emp?.overtime || []).map((o) => `${o.from} - ${o.to}`);
      const overtimeType = emp?.overtime?.[0]?.type || null;

      const kObj = karensPerDate?.[String(sh.date)] || null;

      // ✅ karens per SHIFT comes from central module (prevents date-duplication automatically)
      const kShift = karensPerShift?.[String(sh._id)] || null;
      const karensHours = karensEnabled ? toNum(kShift?.karensHours, 0) : 0;

      const totalH = overlapAdjustedGrossH + otH - breakH - absenceHours;

      const obDet = {};
      const obPay = {};
      (emp?.obDetails || []).forEach((o) => {
        const r = (obs || []).find((x) => String(x._id) === String(o.obType));
        if (!r) return;

        const id = r._id.toString();

        console.log(o)
        console.log(r)

        if (!resolvedObActivated) {
          obDet[id] = 0;
          obPay[id] = 0;
          return;
        }

        const hours = Number(o.obHours || 0);
        const type = r.type || "fixed";
        const value = Number(r.value ?? r.additionalPay ?? 0);

        obDet[id] = hours;

        // ✅ CORE LOGIC
        if (type === "fixed") {
          // ex: 50 kr/h
          obPay[id] = hours * value;

        } else if (type === "percentage") {
          // ex: 100% → double pay
          obPay[id] = hours * payRate * (value / 100);

        } else {
          obPay[id] = 0;
        }
      });

      const baseSal = payType === "monthly" ? 0 : overlapAdjustedGrossH * payRate;
      const breakDed = payType === "monthly" ? 0 : breakH * payRate;

      let otBonus = 0;
      (emp?.overtime || []).forEach((o) => {
        const r = (overtimeRules || []).find((x) => String(x._id) === String(o.type));
        if (!r || !isValidTime(o.from) || !isValidTime(o.to)) return;
        const [fH, fM] = o.from.split(':').map(Number);
        const [tH, tM] = o.to.split(':').map(Number);
        let f = fH * 60 + fM, t = tH * 60 + tM;
        if (t < f) t += 1440;
        const multiplier = 1 + (Number(r.percentage || 0) / 100);
        otBonus += ((t - f) / 60) * payRate * multiplier;
      });

      const obTotalPay = Object.values(obPay).reduce((a, b) => a + (Number(b) || 0), 0);

      let absenceLoss = 0;
      (emp?.absences || []).forEach(abs => {
        const rule = (absenceRules || []).find(r => String(r._id) === String(abs.absenceType));
        if (!rule) return;
        const percentage = Number(rule.percentage || 0);
        if (percentage >= 100) return;
        const hours = abs.appliesWholeShift ? grossH : calculateAbsenceHoursLocal(abs);
        const lostPercentage = (100 - percentage) / 100;
        absenceLoss += hours * payRate * lostPercentage;
      });

      const absenceRanges = (emp?.absences || [])
        .map(abs => abs.appliesWholeShift
          ? `${sh.startTime} - ${sh.endTime}`
          : (abs.from && abs.to ? `${abs.from} - ${abs.to}` : null))
        .filter(Boolean);

      const absenceNames = (emp?.absences || []).map(abs => {
        const rule = (absenceRules || []).find(a => String(a._id) === String(abs.absenceType));
        return rule?.name || 'Frånvaro';
      });

      const beforeVac = baseSal - breakDed + obTotalPay + otBonus - absenceLoss;
      const vacAmount = beforeVac * vacRate;
      const totalPay = beforeVac + vacAmount;

      const wd = new Date(sh.date).toLocaleDateString('sv-SE', { weekday: 'short' });
      const dayLabel = `${wd.charAt(0).toUpperCase() + wd.slice(1)} ${sh.date.split('-')[2]}`;

      const normHours = overlapAdjustedGrossH - breakH - absenceHours;

      return {
        day: dayLabel,
        date: sh.date,
        shiftId: sh._id,
        shift: sh,
        empId: emp?.employeeId,
        emp,
        workplace: (projects || []).find((p) => String(p._id) === String(sh.project))?.name || '',
        projectId: sh.project,
        startTime: sh.startTime,
        endTime: sh.endTime,

        breaks: emp?.breaks || [],
        breakRange: breakR,
        breakTime: breakH,

        absences: absencesWithHours,
        absenceRanges,
        absenceNames,
        absenceHours,

        karensHours,
        karensMeta: kObj || null,

        grossTime: grossH,
        overlapAdjustedGrossTime: overlapAdjustedGrossH - breakH - absenceHours,

        overtime: emp?.overtime || [],
        overtimeHours: otH,
        overtimeRanges: otRanges,
        overtimeType,

        totalWorked: totalH,

        obDetails: obDet,
        obDetailsPay: obPay,
        obTotalPay,

        baseSalary: baseSal,
        breakDeduction: breakDed,
        overtimePay: otBonus,
        vacationPay: vacAmount,
        totalPay,
        absenceLoss,

        normHours,
      };
    });

    setRows(out);
    calculateObTotals(out);
    calculateObPayTotals(out);
  };

  // --- Totals for existing tfoot (kept) ---
  const totalBreak = rows.reduce((a, r) => a + (Number(r.breakTime) || 0), 0);
  const totalGross = rows.reduce((a, r) => a + (Number(r.grossTime) || 0), 0);
  const totalWorked = rows.reduce((a, r) => a + (Number(r.totalWorked) || 0), 0);

  const totalBaseSalaryRaw = rows.reduce((a, r) => a + (Number(r.baseSalary) || 0), 0);
  const totalBreakDeductionRaw = rows.reduce((a, r) => a + (Number(r.breakDeduction) || 0), 0);
  const totalOvertimePayRaw = rows.reduce((a, r) => a + (Number(r.overtimePay) || 0), 0);
  const totalVacationPayRaw = rows.reduce((a, r) => a + (Number(r.vacationPay) || 0), 0);
  const totalSalaryRaw = rows.reduce((a, r) => a + (Number(r.totalPay) || 0), 0);
  const totalAbsenceLossRaw = rows.reduce((a, r) => a + (Number(r.absenceLoss) || 0), 0);

  const totalAbsenceHours = rows.reduce((sum, r) => sum + (Number(r.absenceHours) || 0), 0);

  // ✅ tfoot overtime total (no manual/auto split)
  const totalOvertimeHours = useMemo(() => {
    return (rows || []).reduce((sum, r) => sum + calcOvertimeArrayHours(r?.overtime), 0);
  }, [rows]);

  // --- Robust rule helpers for totals panel ---
  const getAbsenceRule = (absenceTypeId) =>
    (absenceRules || []).find(r => String(r._id) === String(absenceTypeId)) || null;

  const getOvertimeRule = (overtimeTypeId) =>
    (allOvertimeRules || overtimeRules || []).find(r => String(r._id) === String(overtimeTypeId)) || null;

  const isSickAbsence = (absenceTypeId) => {
    const id = String(absenceTypeId || "");
    const systemKey = (absenceSystemKeyById?.[id] || "").toString().trim().toLowerCase();
    if (systemKey === "sick") return true;

    const ruleName = (absenceNameById?.[id] || getAbsenceRule(id)?.name || "").toString().toLowerCase();
    return ruleName.includes("sjuk");
  };

  // ✅ Totals across absences (we'll render them in a better UX below)
  const absenceTotals = useMemo(() => {
    const byType = {};

    (absenceRules || []).forEach((r) => {
      if (!r?._id) return;
      const id = String(r._id);
      byType[id] = {
        id,
        name: r.name || "Frånvaro",
        systemKey: r.systemKey || null,
        hours: 0,
      };
    });

    (rows || []).forEach((r) => {
      (r.absences || []).forEach((abs) => {
        const id = String(abs?.absenceType || "");
        if (!id) return;

        if (!byType[id]) {
          byType[id] = {
            id,
            name: absenceNameById?.[id] || "Frånvaro",
            systemKey: absenceSystemKeyById?.[id] || null,
            hours: 0,
          };
        }

        byType[id].hours += Number(abs?.hours || 0);
      });
    });

    const totalHours = Object.values(byType).reduce(
      (sum, x) => sum + Number(x.hours || 0),
      0
    );

    const sickHours = Object.values(byType)
      .filter((x) => String(x.systemKey || "") === "sick" || isSickAbsence(x.id))
      .reduce((sum, x) => sum + Number(x.hours || 0), 0);

    return { byType, totalHours, sickHours };
  }, [rows, absenceRules, absenceNameById, absenceSystemKeyById]);

  const overtimeTotals = useMemo(() => {
    const byType = {};

    (allOvertimeRules || overtimeRules || []).forEach((r) => {
      if (!r?._id) return;
      const id = String(r._id);
      byType[id] = { id, name: r.name || "Övertid", hours: 0 };
    });

    (rows || []).forEach((r) => {
      (r.overtime || []).forEach((ot) => {
        const id = String(ot?.type || "");
        if (!id) return;

        if (!byType[id]) {
          const rule = getOvertimeRule(id);
          byType[id] = { id, name: rule?.name || "Övertid", hours: 0 };
        }

        const [fh, fm] = String(ot?.from || "00:00").split(":").map(Number);
        const [th, tm] = String(ot?.to || "00:00").split(":").map(Number);
        let from = fh * 60 + fm;
        let to = th * 60 + tm;
        if (to < from) to += 1440;

        byType[id].hours += (to - from) / 60;
      });
    });

    const sorted = Object.values(byType).sort((a, b) =>
      (a.name || "").localeCompare((b.name || ""), "sv-SE")
    );

    const totalHours = sorted.reduce((sum, x) => sum + Number(x.hours || 0), 0);

    return { byType, sorted, totalHours };
  }, [rows, allOvertimeRules, overtimeRules]);

  // ✅ SICK/KARENS totals (single source of truth)
  const totalSickHours = Number(absenceTotals?.sickHours || 0);

  const totalKarensHours = karensEnabled
    ? (rows || []).reduce((sum, r) => sum + Number(r?.karensHours || 0), 0)
    : 0;

  const totalPaidSickHours = karensEnabled
    ? Math.max(totalSickHours - totalKarensHours, 0)
    : totalSickHours;

  const allObs = (obs || [])
    .slice()
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .map((o) => ({
      id: o._id.toString(),
      name: o.name,
      startTime: o.startTime,
      endTime: o.endTime,
      daysApplied: !o.specialDatesSelected
        ? (o.daysApplied || []).map((d) => dayAbbrevMap[d.toLowerCase()] || d)
        : [],
      specialHolidayNames: o.specialDatesSelected ? (o.specialHolidayNames || []) : [],
      specialDatesSelected: o.specialDatesSelected
    }));

  const timeColumnsCount = 7 + allObs.length;

  const isMonthlyPay = payType === "monthly";
  const vacRateForTotals =
    vacationPay?.vacationPayActive && resolvedVacationActivated
      ? vacationPay.vacationPayPercentage / 100
      : 0;

  const obPayTotalsSum = Object.values(obPayTotals || {}).reduce((s, v) => s + (Number(v) || 0), 0);

  const beforeVacMonthly = isMonthlyPay
    ? (monthlyAmount + obPayTotalsSum + totalOvertimePayRaw - totalAbsenceLossRaw)
    : 0;

  const monthlyVacationPay = isMonthlyPay
    ? (beforeVacMonthly * vacRateForTotals)
    : 0;

  const totalBaseSalary = isMonthlyPay ? monthlyAmount : totalBaseSalaryRaw;
  const totalBreakDeduction = isMonthlyPay ? 0 : totalBreakDeductionRaw;
  const totalOvertimePay = totalOvertimePayRaw;
  const totalVacationPay = isMonthlyPay ? monthlyVacationPay : totalVacationPayRaw;
  const totalAbsenceLoss = totalAbsenceLossRaw;
  const totalSalary = isMonthlyPay ? (beforeVacMonthly + monthlyVacationPay) : totalSalaryRaw;

  // ✅ Used lists for compact bottom panel
  const usedAbsenceRows = useMemo(() => {
    const items = Object.values(absenceTotals?.byType || {})
      .filter((x) => Number(x?.hours || 0) > 0)
      .sort((a, b) => (a?.name || "").localeCompare((b?.name || ""), "sv-SE"));
    return items;
  }, [absenceTotals]);

  const usedNonSickAbsenceRows = useMemo(() => {
    return (usedAbsenceRows || []).filter((x) => String(x?.systemKey || "") !== "sick" && !isSickAbsence(x?.id));
  }, [usedAbsenceRows, absenceRules, absenceNameById, absenceSystemKeyById]);

  const usedOvertimeRows = useMemo(() => {
    const items = Object.values(overtimeTotals?.byType || {})
      .filter((x) => Number(x?.hours || 0) > 0)
      .sort((a, b) => (a?.name || "").localeCompare((b?.name || ""), "sv-SE"));
    return items;
  }, [overtimeTotals]);

  return (
    <div style={backdropStyle}>
      {showPayData ? (
        <div style={modalStyle}>
          {/* HEADER LÖNRAPPORT */}
          <div style={{ marginBottom: 30 }}>
            <h2 style={{ fontSize: 30 }}>Lönrapport för {employee?.name || ""}</h2>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={handlePrevMonth}
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#111827",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {"<"}
              </button>

              <h3
                style={{
                  fontSize: 20,
                  color: "#666",
                  width: 170,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {currentMonth} {localSelectedYear}
              </h3>

              <button
                onClick={handleNextMonth}
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#111827",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {">"}
              </button>

              {selectedTimeSchema ? (
                <span style={badgeStyle} className="hide-in-print">
                  {allTimeSchemas.find((s) => s._id === selectedTimeSchema)?.name}
                </span>
              ) : null}
            </div>

            <div style={buttonRowStyle}>
              <button
                onClick={() => setShowPayData(false)}
                disabled={!showPayData}
                style={toggleButtonStyle(!showPayData)}
              >
                Tidsrapport
              </button>
              <button
                onClick={() => setShowPayData(true)}
                disabled={showPayData}
                style={toggleButtonStyle(showPayData)}
              >
                Lönrapport
              </button>
              <button onClick={() => exportToExcel('pay')} style={downloadButtonStyle}>
                Ladda ner Lönerapport
              </button>
            </div>
          </div>

          {/* LÖNERAPPORT-TABELL */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead style={stickyHeaderStyle}>
                <tr>
                  <th style={{ ...cellStyle, width: 90 }}>Datum</th>
                  <th style={cellStyle}>Arbetsplats</th>
                  <th style={cellStyle}>Arbetstid</th>
                  <th style={cellStyle}>Bruttolön</th>
                  <th style={cellStyle}>Rastavdrag</th>
                  <th style={cellStyle}>Övertidstillägg</th>
                  <th style={cellStyle}>Semesterersättning</th>
                  {allObs.map((o) => (
                    <th key={o.id} style={cellStyle}>
                      <div style={{ fontWeight: 600 }}>{o.name}</div>
                      <div style={obDaysScrollStyle}>
                        {o.specialDatesSelected
                          ? o.specialHolidayNames.join(', ')
                          : o.daysApplied.join(', ')}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>
                        {o.startTime}–{o.endTime}
                      </div>
                    </th>
                  ))}
                  <th style={cellStyle}>Frånvaro</th>
                  <th style={cellStyle}>Total ersättning</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...cellStyle, textAlign: 'left' }}>
                      {r.day || <span style={dashCenterStyle}>-</span>}
                    </td>

                    <td style={{ ...cellStyle, textAlign: 'left' }}>
                      {r.workplace || <span style={dashCenterStyle}>-</span>}
                    </td>

                    <td style={cellStyle}>
                      {r.day ? (
                        <>
                          {r.startTime} - {r.endTime}
                          {(r.overtimeRanges || []).length > 0 && (
                            <span style={{ fontSize: 12, color: '#5372fc', marginLeft: 6, fontWeight: 500 }}>
                              + {h1(r.overtimeHours)}h ÖT
                            </span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </td>

                    <td style={cellStyle}>{r.day ? kr2(r.baseSalary) : '-'}</td>
                    <td style={cellStyle}>
                      {r.day ? (
                        `${r.breakDeduction === 0 ? h2(r.breakDeduction) : `-${h2(r.breakDeduction)}`}kr`
                      ) : '-'}
                    </td>
                    <td style={cellStyle}>{r.day ? kr2(r.overtimePay) : '-'}</td>
                    <td style={cellStyle}>{r.day ? kr2(r.vacationPay) : '-'}</td>

                    {allObs.map((o) => (
                      <td key={o.id} style={cellStyle}>
                        {r.day ? kr2(r.obDetailsPay?.[o.id] || 0) : '-'}
                      </td>
                    ))}

                    <td style={cellStyle}>{r.day ? `- ${h2(r.absenceLoss)}kr` : '-'}</td>
                    <td style={cellStyle}>{r.day ? kr2(r.totalPay) : '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={totalCellStyle}>Summa</td>
                  <td style={totalCellStyle}>{kr2(totalBaseSalary)}</td>
                  <td style={totalCellStyle}>- {h2(totalBreakDeduction)}kr</td>
                  <td style={totalCellStyle}>{kr2(totalOvertimePay)}</td>
                  <td style={totalCellStyle}>{kr2(totalVacationPay)}</td>
                  {allObs.map((o) => (
                    <td key={o.id} style={totalCellStyle}>
                      {kr2(obPayTotals?.[o.id] || 0)}
                    </td>
                  ))}
                  <td style={totalCellStyle}>- {h2(totalAbsenceLoss)}kr</td>
                  <td style={totalCellStyle}>{kr2(totalSalary)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* FOOTER */}
          <div style={footerStyle}>
            <div style={{ fontWeight: 'bold', fontSize: 16 }}>
              Total Ersättning: {kr2(totalSalary)}
              {isMonthlyPay && (
                <span style={{ marginLeft: 16 }}>
                  Månadslön (grund): {kr2(monthlyAmount)}
                </span>
              )}

              {resolvedObActivated ? (
                allObs.map((o) => (
                  <span key={o.id} style={{ marginLeft: 16 }}>
                    {o.name}: {kr2(obPayTotals?.[o.id] || 0)}
                  </span>
                ))
              ) : (
                <span style={{ marginLeft: 16 }}>
                  OB: avaktiverat
                </span>
              )}

              {!resolvedVacationActivated && (
                <span style={{ marginLeft: 16 }}>
                  Semesterersättning: avaktiverat
                </span>
              )}
            </div>

            <button onClick={onClose} style={bottomCloseButtonStyle}>
              Stäng
            </button>
          </div>
        </div>
      ) : (
        <div
          style={modalStyle}
          ref={printRef}
          className={`time-report-print ${includeTotalsInPrint ? "" : "print-hide-totals"}`}
        >
          <div className="print-page-number" />

          {/* HEADER TIDSRAPPORT */}
          <div style={{ marginBottom: 30 }} className="hide-original-header-print">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row' }}>
              <h2 style={{ fontSize: 30 }}>Tidsrapport för {employee?.name || ""}</h2>



{!readOnly && (
  isEditing === true ? (
    <div>
      <button
        onClick={handleSubmitEdits}
        style={{
          backgroundColor: '#007BFF',
          color: '#FFFFFF',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Spara ändringar
      </button>

      <button
        onClick={cancelEditing}
        style={{
          backgroundColor: '#007BFF',
          color: '#FFFFFF',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          marginLeft: '8px'
        }}
      >
        Avbryt
      </button>
    </div>
  ) : (
    <button
      onClick={startEditing}
      style={{
        backgroundColor: '#007BFF',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: 14,
        padding: '8px 20px',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        transition: 'background-color 0.15s ease',
      }}
    >
      Redigera
    </button>
  )
)}



            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={isEditing ? undefined : handlePrevMonth}
                disabled={isEditing}
                className="hide-in-print"
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#111827",
                  cursor: isEditing ? "default" : "pointer",
                  userSelect: "none",
                  opacity: isEditing ? 0 : 1,
                  pointerEvents: isEditing ? "none" : "auto",
                }}
              >
                {"<"}
              </button>

              <h3
                className="hide-in-print"
                style={{
                  fontSize: 20,
                  color: "#666",
                  width: 170,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {currentMonth} {localSelectedYear}
              </h3>

              <button
                onClick={isEditing ? undefined : handleNextMonth}
                disabled={isEditing}
                className="hide-in-print"
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#111827",
                  cursor: isEditing ? "default" : "pointer",
                  userSelect: "none",
                  opacity: isEditing ? 0 : 1,
                  pointerEvents: isEditing ? "none" : "auto",
                }}
              >
                {">"}
              </button>

              {selectedTimeSchema ? (
                <span style={badgeStyle} className="hide-in-print">
                  {allTimeSchemas.find((s) => s._id === selectedTimeSchema)?.name}
                </span>
              ) : null}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                marginTop: 20,
              }}
            >
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => setShowPayData(false)}
                  disabled={!showPayData}
                  style={toggleButtonStyle(!showPayData)}
                >
                  Tidsrapport
                </button>

                <button
                  onClick={() => setShowPayData(true)}
                  disabled={showPayData || isEditing}
                  style={{
                    ...toggleButtonStyle(showPayData),
                    ...(showPayData || isEditing
                      ? {
                        opacity: 0.45,
                        cursor: "not-allowed",
                        pointerEvents: "none",
                      }
                      : {}),
                  }}
                >
                  Lönrapport
                </button>
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: "center" }}>
                <button
                  onClick={openPrintConfirm}
                  disabled={isEditing}
                  style={{
                    ...downloadButtonStyle,
                    ...(isEditing
                      ? {
                        opacity: 0.45,
                        cursor: "not-allowed",
                        pointerEvents: "none",
                      }
                      : {}),
                  }}
                >
                  Skriv ut (PDF)
                </button>

                <button
                  onClick={() => exportToExcel("time")}
                  disabled={isEditing}
                  style={{
                    ...downloadButtonStyle,
                    ...(isEditing
                      ? {
                        opacity: 0.45,
                        cursor: "not-allowed",
                        pointerEvents: "none",
                      }
                      : {}),
                  }}
                >
                  Ladda ner (Excell)
                </button>
              </div>
            </div>
          </div>

          {/* TIDSRAPPORT-TABELL */}
          <div style={{ flex: 1, overflowY: 'auto' }} className="time-report-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead style={stickyHeaderStyle}>
                <tr className="print-only-header" style={{ display: "none" }}>
                  <th colSpan={timeColumnsCount}>
                    <div className="print-header-wrapper">
                      <h2 className="print-header-title">Tidsrapport för {employee?.name || ""}</h2>
                      <h3 className="print-header-subtitle">{currentMonth} {localSelectedYear}</h3>
                    </div>
                  </th>
                </tr>

                <tr style={{ position: 'relative' }}>
                  <th style={{ ...cellStyle, width: 90 }}>Datum</th>
                  <th style={cellStyle}>Arbetsplats</th>
                  <th style={cellStyle}>Arbetstid</th>
                  <th style={cellStyle}>Rast</th>
                  <th style={cellStyle}>Frånvaro</th>
                  <th style={cellStyle}>Övertid</th>
                  <th style={cellStyle}>Arbetad tid</th>
                  {allObs.map((o) => (
                    <th key={o.id} style={cellStyle}>
                      <div style={{ fontWeight: 600 }}>{o.name}</div>

                      <div style={obDaysScrollStyle} className="ob-days">
                        {o.specialDatesSelected
                          ? o.specialHolidayNames.join(', ')
                          : o.daysApplied.join(', ')}
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 500 }} className="ob-time">
                        {o.startTime}–{o.endTime}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.map((r, i) => {
                  const isActive =
                    showEditShiftPopup &&
                    selectedShiftData?.shift?._id === r.shiftId &&
                    selectedShiftData?.empId === r.empId;

                  const hasShift = Boolean(r.shiftId && r.empId && r.day);
                  const showKarensChip = karensEnabled && Number(r.karensHours || 0) > 0;

                  return (
                    <tr key={i} style={isActive ? { backgroundColor: '#fff7cc' } : {}}>
                      <td style={cellStyle}>{r.day || '-'}</td>
                      <td style={{ ...cellStyle, textAlign: 'left' }}>
                        {r.workplace || <span style={dashCenterStyle}>-</span>}
                      </td>

                      <td style={{ ...cellStyle, whiteSpace: 'nowrap', padding: '4px 6px' }}>
                        {r.startTime && r.endTime ? `${r.startTime} - ${r.endTime}` : '-'}
                        {isEditing && hasShift && (
                          <p
                            style={editShiftDataButton}
                            onClick={(e) => handleClickedEditShift(e, 'shift', r.shiftId, r.empId)}
                          >
                            [ redigera ]
                          </p>
                        )}
                      </td>

                      <td style={{ ...cellStyle, padding: '4px 6px' }}>
                        {r.breaks?.length > 0 ? (
                          r.breaks.map((brk, idx) => (
                            <div key={idx} style={breakRowStyle}>
                              <span>{brk.from} – {brk.to}</span>
                              {isEditing && hasShift && (
                                <FaEdit
                                  style={editIconStyle}
                                  onClick={(e) => {
                                    const rect = e.target.getBoundingClientRect();
                                    setEditType('break');
                                    setSelectedShiftData({
                                      shift: r.shift,
                                      empId: r.empId,
                                      editIndex: idx
                                    });
                                    setPopupPosition({ x: rect.right + 6, y: rect.top });
                                    setShowEditShiftPopup(true);
                                  }}
                                />
                              )}
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}

                        {isEditing && hasShift && (
                          <p
                            style={editShiftDataButton}
                            onClick={(e) => {
                              const rect = e.target.getBoundingClientRect();
                              setEditType('break');
                              setSelectedShiftData({
                                shift: r.shift,
                                empId: r.empId,
                                editIndex: null
                              });
                              setPopupPosition({ x: rect.right + 6, y: rect.top });
                              setShowEditShiftPopup(true);
                            }}
                          >
                            [ lägg till ]
                          </p>
                        )}
                      </td>

                      <td style={{ ...cellStyle, padding: '4px 6px' }} className="absence-cell">
                        {r.absences?.length > 0 ? (
                          <>
                            {r.absences.map((abs, idx) => {
                              const ruleName = absenceNameById[String(abs.absenceType)] || 'Frånvaro';
                              const systemKey = absenceSystemKeyById[String(abs.absenceType)] || null;

                              const displayName = shortAbsenceLabel(ruleName, systemKey);

                              const timeLabel = abs.appliesWholeShift
                                ? `${r.startTime} - ${r.endTime}`
                                : `${abs.from} - ${abs.to}`;

                              const sick = isSickAbsence(abs.absenceType);

                              return (
                                <div key={idx} style={absenceRowStyle}>
                                  <span
                                    style={sick ? sickAbsencePillStyle : absencePillStyle}
                                    title={ruleName}
                                    className="absence-label"
                                  >
                                    {displayName}
                                  </span>

                                  <span style={absenceTimeStyle} className="absence-time">
                                    {timeLabel}
                                  </span>

                                  {isEditing && hasShift && (
                                    <FaEdit
                                      style={editIconStyle}
                                      onClick={(e) => {
                                        const rect = e.target.getBoundingClientRect();
                                        setEditType('absence');
                                        setSelectedShiftData({
                                          shift: r.shift,
                                          empId: r.empId,
                                          editIndex: idx,
                                        });
                                        setPopupPosition({ x: rect.right + 6, y: rect.top });
                                        setShowEditShiftPopup(true);
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}

                            {showKarensChip ? (
                              <div style={{ ...absenceRowStyle, marginTop: 6 }}>
                                <span
                                  style={karensPillStyle}
                                  title={`K: ${h2(r.karensHours)}h`}
                                >
                                  {`Karens ${h2(r.karensHours)}h`}
                                </span>
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span>-</span>
                        )}

                        {isEditing && hasShift && (
                          <p
                            style={editShiftDataButton}
                            onClick={(e) => {
                              const rect = e.target.getBoundingClientRect();
                              setEditType('absence');
                              setSelectedShiftData({
                                shift: r.shift,
                                empId: r.empId,
                                editIndex: null,
                              });
                              setPopupPosition({ x: rect.right + 6, y: rect.top });
                              setShowEditShiftPopup(true);
                            }}
                          >
                            [ lägg till ]
                          </p>
                        )}
                      </td>

                      <td style={{ ...cellStyle, padding: '4px 6px' }} className="overtime-cell">
                        {r.overtime?.length > 0 ? (
                          r.overtime.map((ot, idx) => {
                            const rule = (allOvertimeRules || []).find((o) => String(o._id) === String(ot.type));
                            const typeName = rule?.name || 'Övertid';
                            return (
                              <div key={idx} style={overtimeRowStyle}>
                                <span style={overtimePillStyle} title={typeName} className="overtime-label">
                                  {typeName}
                                </span>
                                <span style={overtimeTimeStyle} className="overtime-time">
                                  {ot.from} – {ot.to}
                                </span>

                                {isEditing && hasShift && (
                                  <FaEdit
                                    style={editIconStyle}
                                    onClick={(e) => {
                                      const rect = e.target.getBoundingClientRect();
                                      setEditType('overtime');
                                      setSelectedShiftData({
                                        shift: r.shift,
                                        empId: r.empId,
                                        editIndex: idx,
                                      });
                                      setPopupPosition({ x: rect.right + 6, y: rect.top });
                                      setShowEditShiftPopup(true);
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <span>-</span>
                        )}

                        {isEditing && hasShift && (
                          <p
                            style={editShiftDataButton}
                            onClick={(e) => {
                              const rect = e.target.getBoundingClientRect();
                              setEditType('overtime');
                              setSelectedShiftData({
                                shift: r.shift,
                                empId: r.empId,
                                editIndex: null,
                              });
                              setPopupPosition({ x: rect.right + 6, y: rect.top });
                              setShowEditShiftPopup(true);
                            }}
                          >
                            [ lägg till ]
                          </p>
                        )}
                      </td>

                      <td style={cellStyle}>{r.day ? `${h2(r.totalWorked)}h` : '-'}</td>

                      {allObs.map((o) => (
                        <td key={o.id} style={cellStyle}>
                          {r.day ? `${h2(r.obDetails?.[o.id] || 0)}h` : '-'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={2} style={totalCellStyle}>Summa</td>
                  <td style={totalCellStyle}>{h2(totalGross)}h</td>
                  <td style={totalCellStyle}>{h2(totalBreak)}h</td>
                  <td style={totalCellStyle}>{h2(totalAbsenceHours)}h</td>
                  <td style={totalCellStyle}>{h2(totalOvertimeHours)}h</td>
                  <td style={totalCellStyle}>{h2(totalWorked)}h</td>
                  {allObs.map((o) => (
                    <td key={o.id} style={totalCellStyle}>
                      {h2(obTotals?.[o.id] || 0)}h
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ✅ Bottom summary redesigned: Sjukfrånvaro now matches Frånvaro layout */}
          <div style={totalsFooterStyle} className="time-report-footer">
            <div style={totalsGridStyle} className="totals-grid">

              {/* Sjukfrånvaro (same layout as Frånvaro/Övertid) */}
              <div style={totalsCardStyle} className="totals-card totals-card--sick">
                <div style={totalsHeaderRowStyle}>
                  <span style={totalsTitleStyle} className="totals-card-title">Sjukfrånvaro</span>
                  <span style={totalsHeaderTotalStyle} className="totals-card-total">
                    Total <strong>{h2(totalSickHours)}h</strong>
                  </span>
                </div>

                <div style={totalsListGridStyle} className="totals-list-grid">
                  <div style={totalsGridItemStyle} className="totals-row" title="Sjuktimmar">
                    <span style={totalsGridItemLabelStyle} className="totals-grid-item-label">Sjuktimmar</span>
                    <strong style={totalsGridItemValueStyle} className="totals-grid-item-value">{h2(totalSickHours)}h</strong>
                  </div>

                  {karensEnabled ? (
                    <>
                      <div style={totalsGridItemStyle} className="totals-row" title="Karensram (max)">
                        <span style={totalsGridItemLabelStyle} className="totals-grid-item-label">Karensram (max)</span>
                        <strong style={totalsGridItemValueStyle} className="totals-grid-item-value">{h2(karensMaxHours)}h</strong>
                      </div>

                      <div style={totalsGridItemStyle} className="totals-row" title="Karens (använd)">
                        <span style={totalsGridItemLabelStyle} className="totals-grid-item-label">Karens (använd)</span>
                        <strong style={totalsGridItemValueStyle} className="totals-grid-item-value">{h2(totalKarensHours)}h</strong>
                      </div>

                      <div style={totalsGridItemStyle} className="totals-row" title="Betalda">
                        <span style={totalsGridItemLabelStyle} className="totals-grid-item-label">Betalda</span>
                        <strong style={totalsGridItemValueStyle} className="totals-grid-item-value">{h2(totalPaidSickHours)}h</strong>
                      </div>
                    </>
                  ) : null}

                </div>
              </div>

              {/* Frånvaro (non-sick) */}
              <div style={totalsCardStyle} className="totals-card">
                <div style={totalsHeaderRowStyle}>
                  <span style={totalsTitleStyle} className="totals-card-title">Frånvaro</span>
                  <span style={totalsHeaderTotalStyle} className="totals-card-total">
                    Total <strong>{h2(Math.max((absenceTotals?.totalHours || 0) - totalSickHours, 0))}h</strong>
                  </span>
                </div>

                <div style={totalsListGridStyle}>
                  {(
                    usedNonSickAbsenceRows.slice(0, 6).map((x) => (
                      <div key={x.id} style={totalsGridItemStyle} className="totals-row" title={x.name}>
                        <span style={totalsGridItemLabelStyle} className="totals-grid-item-label">
                          {shortAbsenceLabel(x.name, x.systemKey)}
                        </span>
                        <strong style={totalsGridItemValueStyle} className="totals-grid-item-value">{h2(x.hours)}h</strong>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Övertid */}
              <div style={totalsCardStyle} className="totals-card">
                <div style={totalsHeaderRowStyle}>
                  <span style={totalsTitleStyle} className="totals-card-title">Övertid</span>
                  <span style={totalsHeaderTotalStyle} className="totals-card-total">
                    Total <strong>{h2(overtimeTotals?.totalHours || 0)}h</strong>
                  </span>
                </div>

                <div style={totalsListGridStyle}>
                  {(
                    usedOvertimeRows.slice(0, 6).map((x) => (
                      <div key={x.id} style={totalsGridItemStyle} className="totals-row" title={x.name}>
                        <span style={totalsGridItemLabelStyle} className="totals-grid-item-label">
                          {x.name}
                        </span>
                        <strong style={totalsGridItemValueStyle} className="totals-grid-item-value">{h2(x.hours)}h</strong>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>

            <button onClick={onClose} style={bottomCloseButtonStyle} className="hide-in-print">
              Stäng
            </button>
          </div>

        </div>
      )}

      {showEditShiftPopup && (
        <EditFromTimeReport
          position={popupPosition}
          onClose={handleEditPopupClose}
          allAbsenceRules={absenceRules}
          allOvertimeRules={overtimeRules}
          shiftData={selectedShiftData?.shift}
          empId={selectedShiftData?.empId}
          editIndex={selectedShiftData?.editIndex}
          editType={editType}
        />
      )}

      {/* ✅ NEW: Print confirm popup (explicit about bottom totals rows) */}
      {showPrintConfirm && (
        <div style={printConfirmBackdropStyle} className="hide-in-print">
          <div style={printConfirmCardStyle}>
            <div style={printConfirmTitleRowStyle}>
              <div style={printConfirmTitleStyle}>Innan du skriver ut</div>

            </div>

            <div style={printConfirmTextStyle}>
              Vill du inkludera <strong>totalpanelen längst ner</strong> i PDF?
              <div style={printConfirmHintStyle}>
                Det är panelen med <strong>Sjukfrånvaro</strong>, <strong>Frånvaro</strong> och <strong>Övertid</strong>
                {karensEnabled ? " (inkl. Karens-raderna)." : "."}
              </div>
            </div>

            <div style={printConfirmOptionsStyle}>
              <label style={printConfirmOptionStyle}>
                <input
                  type="radio"
                  name="printTotals"
                  checked={Boolean(pendingIncludeTotalsInPrint) === true}
                  onChange={() => setPendingIncludeTotalsInPrint(true)}
                />
                <span>Inkludera totalpanelen längst ner i PDF</span>
              </label>

              <label style={printConfirmOptionStyle}>
                <input
                  type="radio"
                  name="printTotals"
                  checked={Boolean(pendingIncludeTotalsInPrint) === false}
                  onChange={() => setPendingIncludeTotalsInPrint(false)}
                />
                <span>Exkludera totalpanelen längst ner i PDF</span>
              </label>
            </div>

            <div style={printConfirmActionsStyle}>
              <button onClick={cancelPrintConfirm} style={printConfirmCancelBtnStyle}>
                Avbryt
              </button>
              <button onClick={confirmAndPrint} style={printConfirmPrimaryBtnStyle}>
                Skriv ut
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// --- Bas-storlekar (ikon blir alltid lite större än texten) ---
const ROW_FONT_PX = 12;
const ICON_SCALE = 1.35;

const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 100000,
};

const modalStyle = {
  backgroundColor: '#fff',
  borderRadius: 8,
  padding: '1.5rem',
  width: '98%',
  maxWidth: '98%',
  maxHeight: '97vh',
  height: '97vh',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 10000,
};

const badgeStyle = {
  fontSize: 12,
  backgroundColor: '#eef1ff',
  color: '#3b4cca',
  padding: '4px 10px',
  borderRadius: 6,
  fontWeight: 500,
};

const buttonRowStyle = {
  display: 'flex',
  gap: 8,
  marginTop: 20,
  alignItems: 'center',
};

const toggleButtonStyle = (active) => ({
  backgroundColor: active ? '#007BFF' : '#fff',
  color: active ? '#fff' : '#007BFF',
  border: '1px solid #007BFF',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: active ? 'default' : 'pointer',
  fontWeight: 600,
  fontSize: 15,
  transition: 'all 0.2s',
});

const downloadButtonStyle = {
  marginLeft: 'auto',
  backgroundColor: '#fff',
  color: '#007BFF',
  border: '1px solid #007BFF',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 15,
  transition: 'all 0.2s',
};

const footerStyle = {
  paddingTop: '1rem',
  borderTop: '1px solid #ddd',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const bottomCloseButtonStyle = {
  padding: '10px 18px',
  minWidth: 120,
  backgroundColor: '#007BFF',
  color: '#fff',
  border: '1px solid rgba(0,0,0,0.06)',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer',
  boxShadow: '0 6px 14px rgba(0, 123, 255, 0.22)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  lineHeight: 1,
  marginTop: 26
};

const cellStyle = {
  border: '1px solid #ddd',
  padding: 10,
  textAlign: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const totalCellStyle = {
  ...cellStyle,
  fontWeight: 'bold',
  backgroundColor: '#f8f8f8',
};

const editShiftDataButton = {
  color: 'grey',
  fontSize: 10,
  cursor: 'pointer',
};

const overtimeRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'nowrap',
};
const overtimePillStyle = {
  display: 'inline-block',
  fontSize: 11,
  lineHeight: 1.4,
  padding: '0px',
  borderRadius: 0,
  backgroundColor: 'transparent',
  color: '#111827',
  maxWidth: 190,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  border: 'none',
  boxShadow: 'none',
  fontWeight: 700,
};
const overtimeTimeStyle = {
  fontSize: 12,
  color: '#444',
};

const absenceRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'nowrap',
};
const absencePillStyle = {
  display: 'inline-block',
  fontSize: 11,
  lineHeight: 1.4,
  padding: '0px',
  borderRadius: 0,
  backgroundColor: 'transparent',
  color: '#111827',
  maxWidth: 210,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  border: 'none',
  boxShadow: 'none',
  fontWeight: 700,
};
const sickAbsencePillStyle = {
  display: 'inline-block',
  fontSize: 11,
  lineHeight: 1.4,
  padding: '2px 6px',
  borderRadius: 9999,
  backgroundColor: '#f3f4f6',
  color: '#111827',
  border: '1px solid #e5e7eb',
  maxWidth: 210,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 800,
};
const absenceTimeStyle = {
  fontSize: 12,
  color: '#444',
};

const karensPillStyle = {
  display: 'inline-block',
  fontSize: 11,
  lineHeight: 1.4,
  padding: '2px 6px',
  borderRadius: 9999,
  backgroundColor: '#f3f4f6',
  color: '#111827',
  border: '1px solid #e5e7eb',
  maxWidth: 210,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 800,
};

const breakRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
};

const editIconStyle = {
  cursor: 'pointer',
  fontSize: '1em',
  color: 'grey',
  flexShrink: 0,
  marginLeft: 2,
  verticalAlign: 'middle',
};

const stickyHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: '#f9fafb',
};

const obDaysScrollStyle = {
  fontSize: 11,
  color: '#444',
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  display: 'block',
  paddingBottom: 2,
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

obDaysScrollStyle['::-webkit-scrollbar'] = {
  display: 'none',
};

const dashCenterStyle = {
  display: 'inline-block',
  width: '100%',
  textAlign: 'center',
};

/* ✅ Bottom summary: smaller height + exactly 2 rows (3 cols) visible */
const totalsFooterStyle = {
  paddingTop: 0,
  borderTop: "1px solid #ddd",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
  maxHeight: 90,
  overflow: "hidden",
};

const totalsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
  gap: 0,
  width: "100%",
  alignItems: "stretch",
};

const totalsCardStyle = {
  border: "1px solid #e5e7eb",
  padding: 8,
  backgroundColor: "#fff",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  height: 82,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};

const totalsHeaderRowStyle = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
  lineHeight: 1.0,
};

const totalsTitleStyle = {
  fontSize: 12,
  fontWeight: 900,
  color: "#111827",
  letterSpacing: 0.2,
  whiteSpace: "nowrap",
};

const totalsHeaderTotalStyle = {
  fontSize: 12,
  color: "#374151",
  whiteSpace: "nowrap",
};

const totalsListGridStyle = {
  marginTop: 6,
  paddingTop: 6,
  borderTop: "1px solid rgba(0,0,0,0.06)",
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 6,
  overflow: "hidden",
  minHeight: 0,
  flex: 1,
};

const totalsGridItemStyle = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
  padding: "0px 2px",
  minWidth: 0,
};

const totalsGridItemLabelStyle = {
  fontSize: 10,
  color: "#374151",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const totalsGridItemValueStyle = {
  fontSize: 10,
  color: "#111827",
  fontWeight: 900,
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const totalsEmptyStyle = {
  gridColumn: "1 / -1",
  fontSize: 12,
  color: "#6b7280",
  paddingTop: 4,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/* ✅ Print confirm popup (screen only) */
const printConfirmBackdropStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(17, 24, 39, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000000,
  padding: 16,
};

const printConfirmCardStyle = {
  width: "100%",
  maxWidth: 520,
  backgroundColor: "#fff",
  borderRadius: 14,
  boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
  border: "1px solid rgba(0,0,0,0.08)",
  padding: 16,
};

const printConfirmTitleRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 10,
};

const printConfirmTitleStyle = {
  fontSize: 16,
  fontWeight: 900,
  color: "#111827",
};


const printConfirmTextStyle = {
  fontSize: 13,
  color: "#111827",
  lineHeight: 1.35,
};

const printConfirmHintStyle = {
  marginTop: 6,
  fontSize: 12,
  color: "#4b5563",
};

const printConfirmOptionsStyle = {
  marginTop: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  paddingTop: 12,
  borderTop: "1px solid rgba(0,0,0,0.08)",
};

const printConfirmOptionStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  fontSize: 13,
  color: "#111827",
  fontWeight: 700,
  cursor: "pointer",
  userSelect: "none",
};

const printConfirmActionsStyle = {
  marginTop: 14,
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  paddingTop: 12,
  borderTop: "1px solid rgba(0,0,0,0.08)",
};

const printConfirmCancelBtnStyle = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.12)",
  backgroundColor: "#fff",
  color: "#111827",
  fontWeight: 800,
  cursor: "pointer",
};

const printConfirmPrimaryBtnStyle = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.06)",
  backgroundColor: "#007BFF",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 10px 22px rgba(0, 123, 255, 0.25)",
};
